// kbContextBuilder — turns the live workspace (device stack / technology)
// into a compact, citation-bearing KB context block for the AI system prompt.
//
// Session C goal: KB-grounded answers that never hallucinate. This builder
// pulls validated parameters, phenomena, validity warnings, and defects from
// the Supabase KB (via kbService) and formats them so the model can only cite
// what's actually in the KB. Fails soft — partial/empty KB ⇒ partial/empty
// context, never a throw.
//
// Two-tier fetch strategy (fixes race condition):
//   All 4 KB calls start simultaneously.
//   Tier 1 callback fires as soon as validity + phenomena + defects complete.
//   Tier 2 callback fires once parameters also completes.
//   This way the caller gets partial context quickly even if parameters is slow,
//   without sequencing the tiers (which would make total time additive).

import {
  getParameters,
  getValidity,
  getPhenomena,
  getDefects,
  getEfficiencyHistory,
  searchKB,
} from './kbService';

// Per-section caps. Tightened from (20/15) to (5/5) for phenomena/defects
// after hitting Anthropic's 10k input-tokens/minute rate limit — most of
// the budget was being burned on family-scope phenomena rows that Claude
// rarely cited. Parameters and warnings kept generous (warnings are
// usually <6, parameters are quasi-essential for setup-mode walkthroughs).
const MAX_PARAMETERS = 20;
const MAX_PHENOMENA = 5;
const MAX_DEFECTS = 5;
const MAX_WARNINGS = 6;

// Hard upper bound on the assembled context length. assembleContext drops
// the lowest-priority sections (family entries first) until the result
// fits, then appends a `[KB context truncated…]` marker listing what was
// dropped. Stops a runaway parameter or phenomena dump from blowing past
// the per-minute token budget on the Anthropic side.
const KB_CONTEXT_BUDGET_CHARS = 3000;

// Status-tag priority (best first) — mirrors the backend resolver ordering.
const STATUS_RANK = {
  VALIDATED: 0, TEXTBOOK: 1, 'REVIEW-CONSENSUS': 2,
  'CIT-NEEDED': 3, INFERRED: 4, 'KBV1-SEED': 5, SEED: 5,
};

function statusRank(status) {
  if (!status) return 6;
  const s = String(status).toUpperCase();
  for (const [token, rank] of Object.entries(STATUS_RANK)) {
    if (s.includes(token)) return rank;
  }
  return 6;
}

function trunc(s, n = 90) {
  if (s == null) return '';
  const str = String(s).replace(/\s+/g, ' ').trim();
  return str.length <= n ? str : str.slice(0, n - 1) + '…';
}

// ── Infer technology family from the absorber material string ──────────────
export function inferTechnology(material) {
  if (!material) return null;
  const m = String(material).toLowerCase();
  if (m.includes('cdte') || m.includes('cdsete') || m.includes('cdznte')) return 'CdTe';
  if (m.includes('cigs') || m.includes('cis') || m.includes('cu(in') || m.includes('cuin')) return 'CIGS';
  if (m.includes('perovskite') || m.includes('mapbi') || m.includes('fapbi') || m.includes('pbi3')) return 'Perovskite';
  if (m.includes('gaas') || m.includes('inp') || m.includes('gainp') || m.includes('iii-v') || m.includes('iiiv')) return 'III-V';
  if (m.includes('si')) return 'Si';  // checked last — 'si' is a common substring
  return material;
}

export function inferMorphology(technology) {
  switch (technology) {
    case 'Si':
    case 'III-V':
      return 'SC';
    case 'CdTe':
    case 'CIGS':
    case 'Perovskite':
      return 'poly';
    default:
      return null;
  }
}

// Mirror of backend ai_analysis._MESSAGE_TECH_KEYWORDS — listed
// most-specific first so a message about "CdSeTe" matches that before
// the family fallback "CdTe". Used to seed KB context when no device
// is loaded so a general-mode question about a specific technology
// still grounds in KB data.
const MESSAGE_TECH_KEYWORDS = [
  'CdSeTe', 'CdTe', 'CIGSe', 'CIGS', 'Perovskite',
  'silicon', 'HJT', 'TOPCon', 'kesterite', 'CZTS',
];

export function extractTechFromMessage(message) {
  if (!message) return null;
  const low = String(message).toLowerCase();
  for (const kw of MESSAGE_TECH_KEYWORDS) {
    if (low.includes(kw.toLowerCase())) return kw;
  }
  return null;
}

// Keywords that mean "the user wants the Green-table efficiency history".
// Hits → buildKBContext fires an extra getEfficiencyHistory(technology)
// query and assembleContext slots the results in as a dedicated
// `=== Efficiency Records (Green Tables) ===` section. Off by default
// because the table is large and irrelevant to most chats.
const EFFICIENCY_KEYWORDS = [
  'efficiency', 'record', 'evolution', 'trajectory',
  'green table', 'progress', 'world record',
  // Comparison / superlative keywords — broaden the trigger so questions
  // like "which technology has the biggest efficiency gap?" or "compare
  // the highest values" fire the efficiency path even without explicit
  // "record"/"green table" wording. Some of these (gap, highest) are
  // generic enough to over-fire on unrelated questions (e.g. "bandgap")
  // — accepted trade-off for broader recall.
  'gap', 'biggest', 'highest', 'which technology', 'compare',
];

export function wantsEfficiencyData(message) {
  if (!message) return false;
  const low = String(message).toLowerCase();
  return EFFICIENCY_KEYWORDS.some(kw => low.includes(kw));
}

// Keywords that mean "the user is asking for a comparison across PV
// technologies". Hits → buildKBContext fires getEfficiencyHistory in
// parallel for each entry in CROSS_TECH_LIST, tags every returned row
// with _query_tech, and formatEfficiency renders per-tech sub-sections
// (1 confirmed + 1 notable per tech) instead of the single-tech 3+all+2
// layout. Off by default — comparison context is materially larger and
// most chats don't need it.
const CROSS_TECH_KEYWORDS = [
  'compare', 'other technologies', 'similar examples',
  'across technologies', 'all technologies',
];

export function wantsCrossTechComparison(message) {
  if (!message) return false;
  const low = String(message).toLowerCase();
  return CROSS_TECH_KEYWORDS.some(kw => low.includes(kw));
}

// Keywords that mean "the user is asking about a physics framework /
// named model / canonical theory". Hits → buildKBContext fires a
// kbService.searchKB(query) call against /kb/search (which queries
// physics_models by model_name/equation/physical_domain/notes plus
// theory_references — see resolver.search_theory at line 227). Results
// are injected into the assembled context as a dedicated section so the
// chat finds entries the existing tech-scoped queries miss (Avarodha
// framework, Shockley-Queisser limit, etc. — these are physics_models
// rows that aren't tagged with a specific PV technology).
const THEORY_KEYWORDS_SPECIFIC = [
  // Distinctive named models — preferred as the search query when matched.
  'Avarodha', 'Shockley-Queisser', 'SQ limit',
];
const THEORY_KEYWORDS_GENERIC = [
  // Generic triggers; used as the query only when no specific name matched.
  'framework', 'model', 'theory',
];

export function wantsTheorySearch(message) {
  if (!message) return false;
  const low = String(message).toLowerCase();
  return THEORY_KEYWORDS_SPECIFIC.some(k => low.includes(k.toLowerCase())) ||
         THEORY_KEYWORDS_GENERIC.some(k => low.includes(k.toLowerCase()));
}

export function extractTheoryQuery(message) {
  if (!message) return null;
  const low = String(message).toLowerCase();
  // Prefer specific named models — those produce a useful ILIKE match
  // against model_name on the backend. Generic words fall back to a
  // looser query that still surfaces model entries containing the term.
  for (const k of THEORY_KEYWORDS_SPECIFIC) {
    if (low.includes(k.toLowerCase())) return k;
  }
  for (const k of THEORY_KEYWORDS_GENERIC) {
    if (low.includes(k.toLowerCase())) return k;
  }
  return null;
}

// Major PV technologies queried in cross-tech comparison mode. The KB tags
// silicon-family rows as "Si" (not "silicon" — `?technology=silicon`
// returns 0 rows), so the query string and display label can differ.
// Order here is the order entries appear in the rendered context block.
const CROSS_TECH_LIST = [
  { query: 'CdTe',       label: 'CdTe' },
  { query: 'CIGS',       label: 'CIGS' },
  { query: 'Perovskite', label: 'Perovskite' },
  { query: 'Si',         label: 'Silicon' },
  { query: 'kesterite',  label: 'Kesterite' },
];

// Filter applied to /kb/efficiency results before they reach the AI: keep
// only rows that look like Martin-Green-style records (or notable exceptions
// or modules), plus any post-2010 row with an actual PCE measurement. Drops
// the long tail of pre-2010 and partial-metric research entries so the AI
// isn't asked to distinguish records from optimisation series.
//
// Spec (verbatim from the user):
//   record_kind IN ('certified', 'notable_exception', 'module')
//   OR (pce_pct is not null AND year >= 2010)
//
// The in-list check now reads the structured `record_kind` column directly
// (migrated from a notes/subcategory regex at commit cd571ee, once the
// column was populated server-side). This is the same source of truth
// formatEfficiency uses for the per-row [CERTIFIED RECORD]/[MODULE]/etc.
// label — single classification, two consumers.
//
// Known limitation (unchanged by this migration): the post-2010 PCE
// fallback still passes the 18 Paudel-2011 thesis rows
// (record_kind='unknown', pce_pct non-null, year=2011). The fallback is
// OR'd with the in-list check so the structured column can't drop them
// by itself. Removing the fallback would fix Paudel but also drop any
// post-2010 record whose record_kind hasn't been classified yet. Kept
// per the original user spec; revisit if the KB classification reaches
// full coverage.
export function isRelevantEfficiencyRow(r) {
  if (!r) return false;
  if (['certified', 'notable_exception', 'module'].includes(r.record_kind)) return true;
  return r.pce_pct != null && r.year != null && r.year >= 2010;
}

export function deriveDeviceInfo(workspace) {
  const dev = workspace?.device_spec || {};
  const absLayers = (dev.absorber && dev.absorber.layers) || [];
  const l0 = absLayers[0] || {};
  const material = l0.material || l0.name || null;
  const technology = inferTechnology(material);
  const morphology = inferMorphology(technology);
  return { material, technology, morphology };
}

// ── Section formatters ─────────────────────────────────────────────────────
function formatParameters(paramObj) {
  const entries = Object.entries(paramObj || {});
  if (!entries.length) return null;
  entries.sort((a, b) => statusRank(a[1].status) - statusRank(b[1].status));
  const lines = entries.slice(0, MAX_PARAMETERS).map(([name, p]) => {
    const label = p.symbol || name;
    const val = p.value != null ? p.value : '—';
    const unit = p.unit && p.unit !== '-' ? ` ${p.unit}` : '';
    const status = p.status ? ` ${p.status}` : '';
    const src = p.source_ref ? ` — ${trunc(p.source_ref, 60)}` : '';
    return `  ${label}: ${val}${unit}${status}${src}`;
  });
  return lines;
}

function formatPhenomena(rows) {
  if (!rows || !rows.length) return null;
  return rows.slice(0, MAX_PHENOMENA).map((ph) => {
    const id = ph.phenomenon_id || 'PH-?';
    const name = ph.phenomenon_name || '';
    const cause = ph.physical_cause ? `: ${trunc(ph.physical_cause, 70)}` : '';
    return `  ${id}: ${name}${cause}`;
  });
}

function formatWarnings(validity) {
  const warnings = (validity && validity.warnings) || [];
  if (!warnings.length) return null;
  return warnings.slice(0, MAX_WARNINGS).map((w) => {
    const msg = w.primary_limitation || w.consequence || w.dd_valid || 'see validity map';
    const cfg = w.device_configuration ? ` (${trunc(w.device_configuration, 40)})` : '';
    return `  ⚠ ${trunc(msg, 90)}${cfg}`;
  });
}

function formatTheory(searchResult) {
  // Renders the {references, models} shape from kbService.searchKB into
  // a compact line list. Caps to keep the section bounded — top-5 models,
  // top-3 references. Returns null when both sides are empty.
  const models = (searchResult && searchResult.models) || [];
  const refs   = (searchResult && searchResult.references) || [];
  if (!models.length && !refs.length) return null;
  const out = [];
  if (models.length) {
    out.push('Physics models:');
    for (const m of models.slice(0, 5)) {
      const name = m.model_name || '?';
      const domain = m.physical_domain ? ` (${m.physical_domain})` : '';
      const eq = m.equation ? ` — ${trunc(m.equation, 60)}` : '';
      out.push(`  ${name}${domain}${eq}`);
    }
  }
  if (refs.length) {
    out.push('References:');
    for (const r of refs.slice(0, 3)) {
      const authors = r.authors || '?';
      const year = r.year != null ? ` (${r.year})` : '';
      const title = r.title ? ` — ${trunc(r.title, 80)}` : '';
      out.push(`  ${authors}${year}${title}`);
    }
  }
  return out;
}

function formatDefects(rows) {
  if (!rows || !rows.length) return null;
  return rows.slice(0, MAX_DEFECTS).map((d) => {
    const notation = d.notation || d.defect_type || '?';
    const level = d.transition_level ? `${d.transition_level} ` : '';
    const type = d.acceptor_or_donor ? `${d.acceptor_or_donor.toLowerCase()}` : '';
    const impact = d.device_impact ? ` — ${trunc(d.device_impact, 60)}` : '';
    return `  ${notation}: ${level}${type}${impact}`;
  });
}

// ── Steps 1-5 of the efficiency pipeline (Master_Table filter, Side+kind
// classifier, dedup with carry-forward + metric fill-through, cert-year
// parse, split+sort). Extracted from formatEfficiency so the cross-tech
// branch can run the same pipeline per-technology. Returns the
// category-split deduped rows plus per-stage counts for logging.
function _runEfficiencyPipeline(rows) {
  // Step 1: filter to Master_Table-sourced rows.
  const masterRows = rows.filter(r => (r.notes || '').includes('Master_Table'));

  // Step 2: classify by record_kind first, Side second. The CIGS bug
  // surfaced 2026-05-29 — Avancis 20.3% (area=526.7 cm² → obviously a
  // module) was being tagged `record_kind='module'` but appeared on
  // `Side: Left` in the table, which the old "Side first" classifier
  // promoted to CONFIRMED_RECORD, pushing the actual 23.35% Solar
  // Frontier cell out of the top-1-confirmed slot. Letting record_kind
  // win when it's strict (`'module'` or `'notable_exception'`) fixes that
  // while preserving Side as the fallback when record_kind is permissive
  // (`'certified'` or `'unknown'`).
  const classified = [];
  for (const r of masterRows) {
    const notes = r.notes || '';
    let kind = null;
    if (r.record_kind === 'module') {
      kind = 'MODULE';
    } else if (r.record_kind === 'notable_exception') {
      kind = 'NOTABLE_EXCEPTION';
    } else if (notes.includes('Side: Left')) {
      // record_kind is 'certified' or 'unknown' — Side: Left = Table 1 cell.
      kind = 'CONFIRMED_RECORD';
    }
    // Side: Right with record_kind in {'certified','unknown'} → no
    // classification (silently dropped — matches prior behavior).
    if (kind) classified.push({ ...r, _kind: kind });
  }

  // Step 3: dedup carry-forward editions by (pce_pct, lab, test_center).
  const groupKey = (r) => `${r.pce_pct}|${r.lab || ''}|${r.test_center || ''}`;
  const groups = new Map();
  for (const r of classified) {
    const k = groupKey(r);
    const yr = (typeof r.year === 'number' && r.year > 0) ? r.year : null;
    let g = groups.get(k);
    if (!g) {
      groups.set(k, { first: r, first_year: yr, last_year: yr, rows: [r] });
      continue;
    }
    g.rows.push(r);
    if (yr != null) {
      if (g.last_year == null || yr > g.last_year) g.last_year = yr;
      if (g.first_year == null || yr < g.first_year) {
        g.first = r;
        g.first_year = yr;
      }
    }
  }

  // Step 4: cert-year parse + metric-field fill-through from later editions.
  // Supports two stamp formats observed in the Green tables:
  //   "(M/YY)"           e.g. "Newport (8/14)"  → 2014
  //   "(MonthName YY)"   e.g. "AIST (November 17)" → 2017
  // YY > 50 routes to 19YY (covers Green-tables history back to ~1992).
  // Without the month-name variant the 23.35% Solar Frontier CIGS record
  // (test_center "AIST (11/18)") lost top-1 to a 22.9% Solar Frontier
  // (test_center "AIST (November 17)") whose year fell back to the
  // year-column edition number rather than the actual 2017 cert date.
  const STAMP = /\((\d{1,2})\/(\d{2})\)/;
  const STAMP_NAMED = /\((Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sept?|Oct|Nov|Dec)[a-z]*\s+(\d{2})\)/i;
  const parseCertYear = (tc) => {
    const s = tc || '';
    let m = STAMP.exec(s);
    if (!m) m = STAMP_NAMED.exec(s);
    if (!m) return null;
    const yy = parseInt(m[2], 10);
    return yy > 50 ? 1900 + yy : 2000 + yy;
  };
  const METRIC_FIELDS = ['pce_pct', 'voc_mv', 'jsc_ma_cm2', 'ff_pct', 'area_cm2'];
  const deduped = Array.from(groups.values()).map(({ first, first_year, last_year, rows: groupRows }) => {
    const ordered = [...groupRows].sort((a, b) => {
      const ya = (typeof a.year === 'number' && a.year > 0) ? a.year : Infinity;
      const yb = (typeof b.year === 'number' && b.year > 0) ? b.year : Infinity;
      return ya - yb;
    });
    const merged = { ...first };
    for (const f of METRIC_FIELDS) {
      if (merged[f] == null) {
        for (const r of ordered) {
          if (r[f] != null) { merged[f] = r[f]; break; }
        }
      }
    }
    const certYr = parseCertYear(merged.test_center);
    return {
      ...merged,
      _first_cert_year: certYr != null ? certYr : first_year,
      _last_year:       last_year,
    };
  });

  // Step 5: split into categories, sort each by first_cert_year desc.
  const byKind = { CONFIRMED_RECORD: [], NOTABLE_EXCEPTION: [], MODULE: [] };
  for (const r of deduped) byKind[r._kind].push(r);
  const yrDesc = (a, b) => (b._first_cert_year || 0) - (a._first_cert_year || 0);
  byKind.CONFIRMED_RECORD.sort(yrDesc);
  byKind.NOTABLE_EXCEPTION.sort(yrDesc);
  byKind.MODULE.sort(yrDesc);

  return {
    byKind,
    masterCount:     masterRows.length,
    classifiedCount: classified.length,
    dedupedCount:    deduped.length,
  };
}

// Step 7 (shared): render one efficiency line with carry-forward annotation
// and category label.
function _renderEfficiencyLine(r) {
  const firstYr = r._first_cert_year;
  const lastYr  = r._last_year;
  const yearLabel = (firstYr && lastYr && lastYr > firstYr)
    ? `${firstYr} (first certified ${firstYr}, carried through ${lastYr})`
    : (firstYr || lastYr || '?');
  const parts = [];
  if (r.pce_pct    != null) parts.push(`PCE=${r.pce_pct}%`);
  if (r.voc_mv     != null) parts.push(`Voc=${r.voc_mv}mV`);
  if (r.jsc_ma_cm2 != null) parts.push(`Jsc=${r.jsc_ma_cm2}mA/cm²`);
  if (r.ff_pct     != null) parts.push(`FF=${r.ff_pct}%`);
  if (r.area_cm2   != null) parts.push(`area=${r.area_cm2}cm²`);
  const meta = `${r.lab || ''} ${r.test_center || ''}`.trim();
  const label = r._kind === 'CONFIRMED_RECORD'   ? '[CONFIRMED RECORD]'
              : r._kind === 'NOTABLE_EXCEPTION'  ? '[NOTABLE EXCEPTION]'
              :                                    '[MODULE]';
  return `  ${yearLabel}${meta ? ' (' + meta + ')' : ''}: ${parts.join(' ') || '—'} ${label}`;
}

function formatEfficiency(rows) {
  if (!rows || !rows.length) return null;

  // Cross-tech detection: when buildKBContext fires queries for multiple
  // technologies (compare-style messages), each row carries _query_tech.
  // ≥2 distinct values → cross-tech rendering with per-tech sub-headers
  // and tighter caps (1 confirmed + 1 notable per tech, no module). One
  // distinct value (or none) → single-tech 3+all+2 layout, unchanged.
  const distinctTechs = new Set();
  for (const r of rows) if (r._query_tech) distinctTechs.add(r._query_tech);
  const crossTech = distinctTechs.size > 1;

  if (crossTech) {
    const out = [];
    let totalMaster = 0, totalClassified = 0, totalDeduped = 0;
    for (const tech of distinctTechs) {
      const techRows = rows.filter(r => r._query_tech === tech);
      const pipe = _runEfficiencyPipeline(techRows);
      totalMaster     += pipe.masterCount;
      totalClassified += pipe.classifiedCount;
      totalDeduped    += pipe.dedupedCount;
      // Lightweight cross-tech cap: top 1 CONFIRMED RECORD per tech, no
      // notable / no module. Cross-tech now only fires from the no-tech
      // fallback path in buildKBContext (per the guaranteed-contract
      // rewrite), where the goal is a thin "what's the record per
      // technology" view — not a deep comparison.
      const techTop = pipe.byKind.CONFIRMED_RECORD.slice(0, 1);
      if (techTop.length) {
        out.push(`${tech}:`);
        for (const r of techTop) out.push(_renderEfficiencyLine(r));
      }
    }
    try {
      /* eslint-disable-next-line no-console */
      console.log('[kbContextBuilder] formatEfficiency [cross-tech]:',
        rows.length, 'rows →',
        totalMaster, 'Master_Table →',
        totalClassified, 'classified →',
        totalDeduped, 'deduped → 1c per tech across ' + distinctTechs.size + ' techs');
    } catch (e) { /* never let logging break the chat */ }
    return out.length ? out : null;
  }

  // Single-tech: per-category quotas (3 confirmed + ALL notable + 2 module).
  const pipe = _runEfficiencyPipeline(rows);
  const top = [
    ...pipe.byKind.CONFIRMED_RECORD.slice(0, 3),
    ...pipe.byKind.NOTABLE_EXCEPTION,
    ...pipe.byKind.MODULE.slice(0, 2),
  ];
  try {
    /* eslint-disable-next-line no-console */
    console.log('[kbContextBuilder] formatEfficiency:',
      rows.length, 'rows →',
      pipe.masterCount, 'Master_Table →',
      pipe.classifiedCount, 'classified →',
      pipe.dedupedCount, 'deduped → top emitting:',
      Math.min(3, pipe.byKind.CONFIRMED_RECORD.length) + 'c + ' +
      pipe.byKind.NOTABLE_EXCEPTION.length + 'n + ' +
      Math.min(2, pipe.byKind.MODULE.length) + 'm');
  } catch (e) { /* never let logging break the chat */ }
  if (!top.length) return null;
  return top.map(_renderEfficiencyLine);
}

// ── Shared context assembler ───────────────────────────────────────────────
// When `specificMaterial` is set and distinct from `technology`, phenomena /
// defects are presented as two labeled subsections (specific first, family
// second) and a SCOPE NOTE prefaces the block. Dedup on phenomenon_id /
// notation: any row matched by both queries (because the KB row's tag field
// substrings into both, e.g. "CdTe;CdSeTe") is shown once in the specific
// section. When no specific material applies, output collapses to the
// single-section layout used before commit 98a863f.
function assembleContext({
  technology, morphology, material, specificMaterial, defaultCdTeNote,
  paramsResp, validity,
  phenomenaFamily, phenomenaSpecific,
  defectsFamily, defectsSpecific,
  efficiencyRecords,
  theoryResult,
}) {
  phenomenaFamily   = phenomenaFamily   || [];
  phenomenaSpecific = phenomenaSpecific || [];
  defectsFamily     = defectsFamily     || [];
  defectsSpecific   = defectsSpecific   || [];
  efficiencyRecords = efficiencyRecords || [];

  // Dedup specific → family by stable id.
  const seenPhIds = new Set();
  const phSpec = phenomenaSpecific.filter(p => {
    const id = p && p.phenomenon_id;
    if (!id) return true;
    if (seenPhIds.has(id)) return false;
    seenPhIds.add(id);
    return true;
  });
  const phFam = phenomenaFamily.filter(p => {
    const id = p && p.phenomenon_id;
    return !(id && seenPhIds.has(id));
  });

  const seenDefKeys = new Set();
  const defSpec = defectsSpecific.filter(d => {
    const k = d && (d.notation || d.defect_type);
    if (!k) return true;
    if (seenDefKeys.has(k)) return false;
    seenDefKeys.add(k);
    return true;
  });
  const defFam = defectsFamily.filter(d => {
    const k = d && (d.notation || d.defect_type);
    return !(k && seenDefKeys.has(k));
  });

  const paramLines      = paramsResp ? formatParameters(paramsResp.parameters) : null;
  const warningLines    = formatWarnings(validity);
  const phSpecLines     = specificMaterial && phSpec.length ? formatPhenomena(phSpec) : null;
  const phFamLines      = formatPhenomena(phFam);
  const defSpecLines    = specificMaterial && defSpec.length ? formatDefects(defSpec) : null;
  const defFamLines     = formatDefects(defFam);
  const efficiencyLines = formatEfficiency(efficiencyRecords);
  const theoryLines     = formatTheory(theoryResult);

  if (!paramLines && !phSpecLines && !phFamLines && !warningLines
      && !defSpecLines && !defFamLines && !efficiencyLines
      && !theoryLines) return '';

  // ── Always-included header chunk ──────────────────────────────────────
  // (header, device tagline, default-CdTe note, SCOPE note) — never dropped
  // by the budget cap; if even this exceeds the budget, the final string
  // is returned as-is rather than mangled.
  const headerLines = ['=== JVISTA Knowledge Base Context ===',
    [`Technology: ${technology || '—'}`,
     `Morphology: ${morphology || '—'}`,
     material ? `Absorber: ${material}` : null].filter(Boolean).join(' | ')];
  if (defaultCdTeNote) headerLines.push(defaultCdTeNote);
  if (specificMaterial) {
    headerLines.push('',
      `SCOPE: User is asking about ${specificMaterial} specifically (a ${technology}-family material).`,
      `Entries below are split into two groups per section: [${specificMaterial}-specific]`,
      `(matched on the exact compound) and [${technology}-family] (matched on the broader`,
      `family, may not hold exactly for ${specificMaterial}). Prefer ${specificMaterial}-specific`,
      `entries when available; cite which scope an entry came from when it matters to the answer.`,
    );
  }
  const headerChunk = headerLines.join('\n');
  const footerChunk = '\n=== End KB Context ===';

  // ── Droppable sections in priority order (keep-first, drop-last) ──────
  // Specific entries beat family entries; validity warnings beat parameter
  // walls; family entries are the first to go when over budget.
  const sections = [];
  const sec = (label, header, lines) => {
    if (lines) sections.push({ label, text: '\n' + header + '\n' + lines.join('\n') });
  };
  sec('phSpec',  `Known Phenomena — ${specificMaterial}-specific:`, phSpecLines);
  sec('defSpec', `Known Defects — ${specificMaterial}-specific:`,   defSpecLines);
  // Efficiency-records section uses the user-specified === header style
  // (distinct from the colon-suffix sections above). High priority — the
  // user has to explicitly ask for it via keyword, so when present they
  // want it. Sits before warn/params so the budget cap protects it.
  sec('efficiency', '=== Efficiency Records (Green Tables) ===', efficiencyLines);
  // Theory / physics-models block — only present when buildKBContext fired
  // a kbService.searchKB() call (gated on framework/model/theory/named-model
  // keywords). High priority so the budget cap protects it.
  sec('theory', '=== Physics Models & Theory ===', theoryLines);
  sec('warn',
      specificMaterial
        ? `Model Validity Warnings — ${technology}-family (no ${specificMaterial}-specific warnings in KB):`
        : 'Model Validity Warnings:',
      warningLines);
  sec('params',
      specificMaterial
        ? `Validated Parameters — ${technology}-family (no ${specificMaterial}-specific rows in KB):`
        : `Validated Parameters (top ${MAX_PARAMETERS} by confidence):`,
      paramLines);
  sec('phFam',
      specificMaterial ? `Known Phenomena — ${technology}-family:`
                       : 'Known Phenomena (relevant to current config):',
      phFamLines);
  sec('defFam',
      specificMaterial ? `Known Defects — ${technology}-family:`
                       : 'Known Defects:',
      defFamLines);

  // ── Greedy include up to KB_CONTEXT_BUDGET_CHARS ──────────────────────
  let total = headerChunk.length + footerChunk.length;
  const included = [];
  const dropped = [];
  for (const s of sections) {
    if (total + s.text.length <= KB_CONTEXT_BUDGET_CHARS) {
      included.push(s); total += s.text.length;
    } else {
      dropped.push(s.label);
    }
  }

  const truncMarker = dropped.length
    ? `\n[KB context truncated to ${KB_CONTEXT_BUDGET_CHARS}-char budget — dropped: ${dropped.join(', ')}]`
    : '';
  return headerChunk
       + included.map(s => s.text).join('')
       + truncMarker
       + footerChunk;
}

// Catch-all KB context emitted when nothing else matches. Per the
// guaranteed-contract rewrite, buildKBContext NEVER returns ''; if
// the single-tech assembled context is empty (KB has nothing for the
// detected tech) or the cross-tech efficiency fan-out comes back empty,
// callers see this string instead.
function _kbFallback() {
  return [
    '=== JVISTA Knowledge Base Context ===',
    'No specific KB data found for this query. Available technologies: CdTe, CIGS, perovskite, silicon, kesterite',
    '=== End KB Context ===',
  ].join('\n');
}

// Lightweight cross-tech context for the no-tech-detected path: top-1
// CONFIRMED RECORD per major PV technology (CROSS_TECH_LIST). Also fires
// a theory/physics-models search when the message contains a relevant
// keyword (Avarodha, framework, etc.) so chats about named frameworks
// still get grounded even with no tech context. Single tier.
async function _buildCrossTechContext(messageHint, onUpdate) {
  const theoryQuery = extractTheoryQuery(messageHint);
  const [effArrs, theoryResult] = await Promise.all([
    Promise.all(CROSS_TECH_LIST.map(({ query, label }) =>
      getEfficiencyHistory(query).then(rs =>
        (rs || []).filter(isRelevantEfficiencyRow).map(r => ({ ...r, _query_tech: label }))
      )
    )),
    theoryQuery ? searchKB(theoryQuery) : Promise.resolve({ references: [], models: [] }),
  ]);
  const efficiencyRecords = effArrs.flat();
  const effLines    = formatEfficiency(efficiencyRecords);
  const theoryLines = formatTheory(theoryResult);
  if ((!effLines || !effLines.length) && (!theoryLines || !theoryLines.length)) {
    const ctx = _kbFallback();
    if (onUpdate) onUpdate(ctx, 'tier1');
    return ctx;
  }
  const parts = [
    '=== JVISTA Knowledge Base Context ===',
    'General context — no specific device or technology detected from your question.',
  ];
  if (effLines && effLines.length) {
    parts.push('', '=== Efficiency Records (Green Tables) ===', ...effLines);
  }
  if (theoryLines && theoryLines.length) {
    parts.push('', '=== Physics Models & Theory ===', ...theoryLines);
  }
  parts.push('=== End KB Context ===');
  const ctx = parts.join('\n');
  if (onUpdate) onUpdate(ctx, 'tier1');
  return ctx;
}

/**
 * Build the KB context block for the current workspace.
 *
 * Contract (post-rewrite — guaranteed to never return ''):
 *   1. Technology detected (from device OR message keyword OR setup-mode
 *      default) → full single-tech context: params, phenomena, defects,
 *      validity warnings, AND efficiency records. Efficiency is no longer
 *      keyword-gated — always included whenever a tech is known.
 *   2. No technology detected → lightweight cross-tech context: top-1
 *      certified record per major PV technology (CdTe, CIGS, Perovskite,
 *      Silicon, Kesterite).
 *   3. Empty result (KB has nothing) → fallback string listing the
 *      available technologies. Callers always receive a non-empty string.
 *
 * Two-tier resolution kept for path 1: Tier 1 onUpdate fires when the
 * fast queries (validity, phenomena, defects, efficiency) resolve;
 * Tier 2 fires when parameters also completes. Path 2 is single-tier.
 *
 * @param {object} workspace  WorkspaceContext value.
 * @param {object} [opts]
 * @param {string} [opts.aiMode]      'general' | 'setup' | 'diagnostic'.
 * @param {string} [opts.messageHint] The user's chat message, used to
 *                                    derive a technology when the device
 *                                    has none.
 * @param {function} [onUpdate]  Called with (ctx, tier) — 'tier1' on
 *                               partial-context, 'tier2' on full context.
 * @returns {Promise<string>} Always a non-empty context string.
 */
export async function buildKBContext(workspace, { aiMode, messageHint } = {}, onUpdate = null) {
  let { material, technology, morphology } = deriveDeviceInfo(workspace);

  // Resolve technology from device → message keyword → setup-mode default.
  // If none of these produce a tech, fall through to the cross-tech path.
  let defaultCdTeNote = null;
  if (!technology) {
    const msgTech = extractTechFromMessage(messageHint);
    if (msgTech) {
      material = msgTech;
      technology = inferTechnology(msgTech);
      morphology = inferMorphology(technology);
      defaultCdTeNote = `[No device loaded — KB context built from "${msgTech}" detected in your question]`;
    } else if (aiMode === 'setup') {
      material = 'CdTe';
      technology = 'CdTe';
      morphology = 'poly';
      defaultCdTeNote = '[Default CdTe context — configure your device for specific values]';
    }
  }

  // ── Path 2: no technology → cross-tech lightweight context ────────────
  if (!technology) {
    return _buildCrossTechContext(messageHint, onUpdate);
  }

  // ── Path 1: technology known → full single-tech context ───────────────
  // Specific-material detection: when material differs from the inferred
  // family (e.g. CdSeTe ↔ CdTe), fire extra material-scoped queries so
  // [CdSeTe-specific] entries are presented separately from [CdTe-family].
  const specificMaterial = (material && material !== technology) ? material : null;

  // Fire ALL queries in parallel. Efficiency is no longer keyword-gated
  // — it always runs when a tech is known (per the rewrite contract).
  // isRelevantEfficiencyRow strips pre-2010 / partial-metric rows after.
  const validityP          = getValidity(technology);
  const phenomenaFamilyP   = getPhenomena(technology);
  const defectsFamilyP     = getDefects(technology);
  const paramsP            = getParameters(material, technology, morphology);
  const phenomenaSpecificP = specificMaterial ? getPhenomena(specificMaterial)
                                              : Promise.resolve([]);
  const defectsSpecificP   = specificMaterial ? getDefects(specificMaterial)
                                              : Promise.resolve([]);
  const efficiencyP = getEfficiencyHistory(technology).then(rs =>
    (rs || []).filter(isRelevantEfficiencyRow)
  );
  // Theory / physics-models search — fires when the message mentions a
  // named model / framework / theory (Avarodha, Shockley-Queisser, etc.)
  // OR generic theory keywords. Hits `/kb/search` which queries both
  // physics_models (by model_name / equation / physical_domain / notes)
  // AND theory_references — see resolver.search_theory(). Off when no
  // keyword matched so the typical chat doesn't pay the round-trip.
  const theoryQuery = extractTheoryQuery(messageHint);
  const theoryP = theoryQuery
    ? searchKB(theoryQuery)
    : Promise.resolve({ references: [], models: [] });

  // Tier 1: fires when fast queries resolve (paramsP runs concurrently).
  const tier1Result = await Promise.all([
    validityP, phenomenaFamilyP, phenomenaSpecificP,
    defectsFamilyP, defectsSpecificP, efficiencyP, theoryP,
  ]).then(([validity, phenomenaFamily, phenomenaSpecific,
            defectsFamily, defectsSpecific, efficiencyRecords,
            theoryResult]) => {
    const ctx = assembleContext({
      technology, morphology, material, specificMaterial, defaultCdTeNote,
      paramsResp: null, validity,
      phenomenaFamily, phenomenaSpecific,
      defectsFamily, defectsSpecific,
      efficiencyRecords, theoryResult,
    });
    if (ctx && onUpdate) onUpdate(ctx, 'tier1');
    return { validity, phenomenaFamily, phenomenaSpecific,
             defectsFamily, defectsSpecific, efficiencyRecords, theoryResult };
  });

  // Tier 2: parameters complete; assemble the full context.
  const paramsResp = await paramsP;
  const {
    validity, phenomenaFamily, phenomenaSpecific,
    defectsFamily, defectsSpecific, efficiencyRecords, theoryResult,
  } = tier1Result;

  const fullCtx = assembleContext({
    technology, morphology, material, specificMaterial, defaultCdTeNote,
    paramsResp, validity,
    phenomenaFamily, phenomenaSpecific,
    defectsFamily, defectsSpecific,
    efficiencyRecords, theoryResult,
  });

  // Guaranteed-contract enforcement: never return ''. If the assembled
  // context comes back empty (KB has zero data for this tech across
  // every section), surface the catch-all fallback instead.
  const finalCtx = fullCtx || _kbFallback();
  if (onUpdate) onUpdate(finalCtx, 'tier2');
  return finalCtx;
}

export default buildKBContext;
